<template>
  <!-- detail 成功后再打开 Create；点 Copy 只应出现 detail，Submit 才 create -->
  <CreateCoupon
    v-if="initialValues"
    copy-mode
    :initial-values="initialValues"
    :eligible-service-options="eligibleServiceOptions"
    @close="emit('close')"
    @saved="emit('saved')"
    @failed="emit('failed')"
  />
</template>

<script setup lang="ts">
import { inject, onMounted, ref } from 'vue'
import {
  mapCouponDetailToCopyInitialValues,
  type CreateCouponInitialValues,
} from '@/api/couponCopy'
import { isCouponApiSuccess } from '@/api/couponApi'
import { postCouponDetail } from '@/api/CouponDetai'
import type { CouponMetaOption } from '@/api/CouponCenter'
import CreateCoupon from '@/views/CouponsPage/CreateCoupon.vue'

const props = defineProps<{
  couponId: string
  /** 列表页已拉过 meta，传入后 Create 不再重复请求 meta */
  eligibleServiceOptions?: CouponMetaOption[]
}>()

const emit = defineEmits<{
  close: []
  saved: []
  failed: []
}>()

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => undefined,
)

const initialValues = ref<CreateCouponInitialValues | null>(null)

onMounted(() => {
  void loadDetailThenOpenCreate()
})

/**
 * Copy = detail 填表 → 用户 Submit 走 create。
 * 打开时只打 /coupon/detail，绝不打 /coupon/copy。
 */
async function loadDetailThenOpenCreate(): Promise<void> {
  const id = props.couponId.trim()
  if (!id) {
    showToast('error', 'Coupon ID is required')
    emit('close')
    return
  }

  try {
    const response = await postCouponDetail({ coupon_id: id })
    if (!isCouponApiSuccess(response.code) || !response.data?.coupon_id) {
      throw new Error(response.msg || 'Failed to load coupon for copy')
    }
    initialValues.value = mapCouponDetailToCopyInitialValues(response.data)
  } catch (loadError) {
    console.error('Load coupon detail for copy error:', loadError)
    let message = 'Failed to load coupon for copy'
    if (loadError instanceof Error && loadError.message.trim()) {
      message = loadError.message.trim()
    } else if (loadError && typeof loadError === 'object' && 'msg' in loadError) {
      const msg = (loadError as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) message = msg.trim()
    }
    showToast('error', message)
    emit('close')
  }
}
</script>
